import { ReactNode, ThHTMLAttributes } from "react";
import { ColumnsType } from "antd/es/table";
import { ColumnGroupType, ColumnType } from "antd/es/table/interface";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "styled-components";

type AnyColumn<T> = ColumnType<T> | ColumnGroupType<T>;

export const reorderColumns = <T,>(
  columns: ColumnsType<T>,
  order: string[],
  trailingKey?: string
): ColumnsType<T> => {
  const byKey = new Map<string, AnyColumn<T>>();
  const trailing: AnyColumn<T>[] = [];

  columns.forEach((col) => {
    const key = String(col.key);
    if (trailingKey && key === trailingKey) {
      trailing.push(col);
      return;
    }
    byKey.set(key, col);
  });

  const ordered: AnyColumn<T>[] = [];
  order.forEach((key) => {
    const col = byKey.get(key);
    if (col) {
      ordered.push(col);
      byKey.delete(key);
    }
  });
  // Append any columns whose keys aren't in `order` (e.g. newly added columns).
  byKey.forEach((col) => ordered.push(col));

  return [...ordered, ...trailing];
};

export type HeaderCellExtraProps = {
  $colKey?: string;
};

const StyledTh = styled.th<{
  $draggable?: boolean;
  $isDragging?: boolean;
  $isOver?: boolean;
}>`
  position: relative;
  ${({ $draggable, $isDragging }) =>
    $draggable && `cursor: ${$isDragging ? "grabbing" : "grab"} !important;`}

  /* AntD overlays the entire th with .ant-table-column-sorters::after
     (position:absolute; inset:0) which steals the cursor. Disable its
     hit-testing so the th's grab cursor shows through; clicks still bubble
     up to the th's sort handler. */
  .ant-table-column-sorters::after {
    pointer-events: none;
  }

  /* The sort icon stays as pointer to read as clickable. */
  .ant-table-column-sorter {
    cursor: pointer !important;
  }
  ${({ $isDragging }) => $isDragging && "opacity: 0.5; z-index: 2;"}
  ${({ $isOver }) =>
    $isOver && "box-shadow: inset 2px 0 0 currentColor; color: #6946d5;"}
`;

type HeaderCellProps = ThHTMLAttributes<HTMLTableCellElement> &
  HeaderCellExtraProps & {
    children?: ReactNode;
  };

export const PrefsHeaderCell: React.FC<HeaderCellProps> = ({
  $colKey,
  style,
  children,
  ...rest
}) => {
  const sortable = useSortable({
    id: $colKey ?? "__no_drag__",
    disabled: !$colKey,
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = sortable;

  const draggable = Boolean($colKey);

  const dragStyle: React.CSSProperties = draggable
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
      }
    : {};

  return (
    <StyledTh
      ref={draggable ? setNodeRef : undefined}
      $draggable={draggable}
      $isDragging={isDragging}
      $isOver={isOver && !isDragging}
      style={{ ...style, ...dragStyle }}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </StyledTh>
  );
};

export const ColumnPrefsDndContext: React.FC<{
  order: string[];
  onOrderChange: (order: string[]) => void;
  children: ReactNode;
}> = ({ order, onOrderChange, children }) => {
  const sensors = useSensors(
    // delay+tolerance instead of pure distance: a normal click — even with a
    // few px of micro-movement — never activates a drag, so sort clicks aren't
    // accidentally consumed as reorders. Press-and-hold to drag.
    useSensor(PointerSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onOrderChange(arrayMove(order, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={order} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
